/**
 * Derive the human-readable "Ask" field labels from a stored columnConfig.
 *
 * "Ask" mode = the fields the AI actively REQUESTS from brokers (see the
 * ColumnMappingStep legend: "Ask — AI will request this info from brokers").
 *
 * ColumnMappingStep.handleConfirm pushes BOTH ask-mode and note-mode canonical
 * keys into `extractionFields`, but note-mode keys are additionally recorded in
 * `neverRequest`. So the true ask set = extractionFields minus neverRequest.
 * Custom (non-canonical) columns live in `customFields` keyed by header, with a
 * mode of "ask_required" / "ask_optional" / "note" / "skip".
 *
 * Labels prefer the original spreadsheet header (`mappings[canonicalKey]`) so the
 * ask reads naturally (e.g. "Total SF") instead of a canonical key ("totalSf").
 *
 * @param {object} columnConfig - stored column config (from AddClientModal).
 * @returns {string[]} de-duplicated, human-readable ask field labels.
 */
export function deriveAskFields(columnConfig) {
  if (!columnConfig || typeof columnConfig !== 'object') return [];

  const {
    extractionFields = [],
    neverRequest = [],
    customFields = {},
    mappings = {},
  } = columnConfig;

  const neverSet = new Set(Array.isArray(neverRequest) ? neverRequest : []);

  const canonical = (Array.isArray(extractionFields) ? extractionFields : [])
    .filter((key) => !neverSet.has(key))
    .map((key) => (mappings && mappings[key]) || key);

  const custom = Object.entries(customFields || {})
    .filter(([, cfg]) => typeof cfg?.mode === 'string' && cfg.mode.startsWith('ask'))
    .map(([header]) => header);

  const seen = new Set();
  return [...canonical, ...custom].filter((label) => {
    if (!label || typeof label !== 'string') return false;
    const norm = label.trim();
    if (!norm) return false;
    const lower = norm.toLowerCase();
    if (seen.has(lower)) return false;
    seen.add(lower);
    return true;
  });
}

/**
 * Flatten a list of campaign properties down to the light shape the
 * generateInitialScript function expects: { address, city, propertyName }.
 * Accepts either an explicit array of properties or a client-like object with
 * `emailToProperties` (a map of email -> property[]).
 *
 * @param {object} source - { properties?: [], emailToProperties?: {} }
 * @returns {Array<{address:string, city:string, propertyName:string}>}
 */
export function deriveCampaignProperties(source) {
  if (!source || typeof source !== 'object') return [];

  let raw = [];
  if (Array.isArray(source.properties)) {
    raw = source.properties;
  } else if (source.emailToProperties && typeof source.emailToProperties === 'object') {
    raw = Object.values(source.emailToProperties).flat();
  }

  const seen = new Set();
  return raw
    .map((p) => ({
      address: typeof p?.address === 'string' ? p.address.trim() : '',
      city: typeof p?.city === 'string' ? p.city.trim() : '',
      propertyName: typeof p?.propertyName === 'string' ? p.propertyName.trim() : '',
    }))
    .filter((p) => {
      if (!p.address && !p.propertyName) return false;
      const key = `${p.propertyName}|${p.address}|${p.city}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}
